import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { FaultReportButton } from "@/components/FaultReportButton";
import {
  LayoutDashboard, Truck, Bell, Users, CreditCard, Settings as Cog,
  Wrench, LogOut, ChevronRight, ShieldCheck, Inbox, ClipboardCheck, BarChart3, ClipboardList, ListChecks, Gift, Crown,
} from "lucide-react";

const NAV = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/prestart", icon: ListChecks, label: "Pre-Start" },
  { to: "/machines", icon: Truck, label: "Machines" },
  { to: "/work-orders", icon: ClipboardCheck, label: "Work Orders" },
  { to: "/reminders", icon: Bell, label: "Reminders", noFree: true },
  { to: "/analytics", icon: BarChart3, label: "Analytics", manager: true },
  { to: "/compliance", icon: ClipboardList, label: "Compliance", manager: true },
  { to: "/notifications", icon: Inbox, label: "Inbox" },
  { to: "/team", icon: Users, label: "Team", manager: true },
  { to: "/billing", icon: CreditCard, label: "Billing", manager: true },
  { to: "/referrals", icon: Gift, label: "Refer & Earn", manager: true },
  { to: "/admin", icon: ShieldCheck, label: "Admin", admin: true },
  { to: "/settings", icon: Cog, label: "Settings" },
];

export default function AppLayout() {
  const { user, fleet, isManager, isFree, logout } = useAuth();
  const nav = useNavigate();
  const items = NAV.filter((n) => (!n.manager || isManager) && (!n.admin || user?.is_admin) && (!n.noFree || !isFree));
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    const load = () => api.get("/notifications").then(({ data }) => setUnread(data.unread)).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    const onRead = () => setUnread(0);
    window.addEventListener("notif-read", onRead);
    return () => { clearInterval(t); window.removeEventListener("notif-read", onRead); };
  }, []);

  return (
    <div className="min-h-screen bg-[#0D0E11] flex">
      {/* Sidebar */}
      <aside className="w-[240px] shrink-0 bg-[#0D0E11] border-r border-white/10 fixed inset-y-0 hidden md:flex flex-col" data-testid="sidebar">
        <div className="h-[68px] flex items-center gap-2.5 px-5 border-b border-white/10">
          <div className="w-8 h-8 bg-[#FFB800] flex items-center justify-center"><Wrench className="w-5 h-5 text-black" strokeWidth={2.5} /></div>
          <span className="font-head font-900 text-2xl tracking-tight">IRONLOG</span>
        </div>
        <div className="px-4 py-4 border-b border-white/10">
          <p className="font-mono text-[10px] text-[#94A3B8] uppercase tracking-widest">Fleet</p>
          <p className="font-head font-700 text-lg truncate">{fleet?.name || "—"}</p>
          <div className="flex items-center gap-2 mt-1">
            {fleet?.plan === "comp" ? (
              fleet?.plan_tier === "Owner" ? (
                <span className="badge flex items-center gap-1 bg-[#FFB800]/15 text-[#FFB800] border-[#FFB800]/40" data-testid="plan-badge"><Crown className="w-3 h-3" /> Owner Access</span>
              ) : (
                <span className="badge flex items-center gap-1 bg-[#00E676]/15 text-[#00E676] border-[#00E676]/40" data-testid="plan-badge"><Gift className="w-3 h-3" /> Complimentary</span>
              )
            ) : (
              <span className="badge badge-upcoming" data-testid="plan-badge">{fleet?.plan_tier || "Free"}</span>
            )}
            <span className="font-mono text-xs text-[#94A3B8]">{fleet?.machine_count}/{fleet?.machine_limit >= 1000000 ? "∞" : fleet?.machine_limit}</span>
          </div>
        </div>
        <nav className="flex-1 min-h-0 overflow-y-auto py-4 px-3 space-y-1">
          {items.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) => `flex items-center gap-3 h-12 px-3 font-head font-600 uppercase tracking-wide text-sm transition-colors ${isActive ? "bg-[#FFB800] text-black" : "text-[#94A3B8] hover:text-white hover:bg-[#1A1C23]"}`}>
              <n.icon className="w-5 h-5 shrink-0" /> {n.label}
              {n.to === "/notifications" && unread > 0 && <span className="ml-auto bg-[#FF3B30] text-white text-[10px] font-mono px-1.5 py-0.5 rounded-full" data-testid="unread-badge">{unread}</span>}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10 shrink-0">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 bg-[#23262F] flex items-center justify-center font-head font-700 text-[#FFB800]">{user?.name?.[0]?.toUpperCase()}</div>
            <div className="min-w-0">
              <p className="font-body font-700 text-sm truncate">{user?.name}</p>
              <p className="font-mono text-[10px] text-[#94A3B8] uppercase">{user?.role}</p>
            </div>
          </div>
          <FaultReportButton />
          <button onClick={() => { logout(); nav("/login"); }} className="btn-ghost w-full h-11 font-head text-sm flex items-center justify-center gap-2" data-testid="logout-btn">
            <LogOut className="w-4 h-4" /> Log Out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 h-14 bg-[#0D0E11] border-b border-white/10 flex items-center justify-between px-4">
        <span className="font-head font-900 text-xl">IRONLOG</span>
        <div className="flex items-center gap-4">
          <FaultReportButton compact />
          <button onClick={() => { logout(); nav("/login"); }} className="text-[#94A3B8]"><LogOut className="w-5 h-5" /></button>
        </div>
      </div>

      <main className="flex-1 md:ml-[240px] pt-14 md:pt-0 min-w-0">
        <Outlet />
      </main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-[#0D0E11] border-t border-white/10 grid grid-cols-5">
        {items.slice(0, 5).map((n) => (
          <NavLink key={n.to} to={n.to} className={({ isActive }) => `flex flex-col items-center justify-center h-16 gap-0.5 ${isActive ? "text-[#FFB800]" : "text-[#94A3B8]"}`}>
            <n.icon className="w-5 h-5" /><span className="font-body text-[10px]">{n.label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}

export function PageHead({ title, sub, children }) {
  return (
    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
      <div>
        <h1 className="font-head font-900 text-4xl md:text-5xl uppercase tracking-tighter leading-none">{title}</h1>
        {sub && <p className="font-body text-[#94A3B8] mt-2">{sub}</p>}
      </div>
      {children && <div className="flex items-center gap-3">{children}</div>}
    </div>
  );
}

export function StatusBadge({ status }) {
  const map = { overdue: "badge-overdue", upcoming: "badge-upcoming", healthy: "badge-healthy", ok: "badge-healthy" };
  const label = status === "ok" ? "healthy" : status;
  return <span className={`badge ${map[status] || "badge-healthy"}`}>{label}</span>;
}
